import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  PlusIcon,
  UserIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { mockMembers, mockSubscriptionPlans } from '../../utils/mockData';

const SubscriptionForm = ({ type = 'subscription' }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    // Subscription fields
    memberId: '',
    planId: '',
    startDate: new Date().toISOString().split('T')[0],
    customPrice: '',
    paymentMethod: 'cash',
    notes: '',
    
    // Plan fields
    name: '',
    description: '',
    type: 'monthly',
    duration: '1 mois',
    price: '',
    features: [''],
    isPopular: false,
    maxMembers: '',
    accessLevel: 'basic',
  });

  // Fetch members for subscription form
  const { data: membersData } = useQuery('members', async () => {
    try {
      const response = await axios.get('/members');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API membres)");
      // Utiliser les données fictives en cas d'erreur
      return { members: mockMembers };
    }
  });

  // Fetch plans for subscription form
  const { data: plansData } = useQuery('subscription-plans', async () => {
    try {
      const response = await axios.get('/subscriptions/plans');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API plans)");
      // Utiliser les données fictives en cas d'erreur
      return { plans: mockSubscriptionPlans };
    }
  });

  // Fetch existing data for editing
  const { data: editData } = useQuery(
    [type, id],
    async () => {
      try {
        if (type === 'plan') {
          const response = await axios.get(`/subscriptions/plans/${id}`);
          return response.data;
        } else {
          const response = await axios.get(`/subscriptions/${id}`);
          return response.data;
        }
      } catch (error) {
        console.warn("Utilisation des données de test en production (échec API détail)");
        // Utiliser un objet vide en cas d'erreur pendant l'édition
        return {};
      }
    },
    { enabled: isEditing }
  );

  // Create/Update mutations
  const createMutation = useMutation(
    (data) => {
      if (type === 'plan') {
        return axios.post('/subscriptions/plans', data);
      } else {
        return axios.post('/subscriptions', data);
      }
    },
    {
      onSuccess: () => {
        toast.success(`${type === 'plan' ? 'Plan' : 'Abonnement'} créé avec succès!`);
        queryClient.invalidateQueries(type === 'plan' ? 'subscription-plans' : 'subscriptions');
        navigate(`/subscriptions${type === 'plan' ? '/plans' : ''}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
        // Si en production et sans backend, simuler un succès
        if (window.location.hostname !== 'localhost') {
          toast.success(`${type === 'plan' ? 'Plan' : 'Abonnement'} créé avec succès! (mode démo)`);
          navigate(`/subscriptions${type === 'plan' ? '/plans' : ''}`);
        }
      },
    }
  );

  const updateMutation = useMutation(
    (data) => {
      if (type === 'plan') {
        return axios.put(`/subscriptions/plans/${id}`, data);
      } else {
        return axios.put(`/subscriptions/${id}`, data);
      }
    },
    {
      onSuccess: () => {
        toast.success(`${type === 'plan' ? 'Plan' : 'Abonnement'} mis à jour avec succès!`);
        queryClient.invalidateQueries(type === 'plan' ? 'subscription-plans' : 'subscriptions');
        navigate(`/subscriptions${type === 'plan' ? '/plans' : ''}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
        // Si en production et sans backend, simuler un succès
        if (window.location.hostname !== 'localhost') {
          toast.success(`${type === 'plan' ? 'Plan' : 'Abonnement'} mis à jour avec succès! (mode démo)`);
          navigate(`/subscriptions${type === 'plan' ? '/plans' : ''}`);
        }
      },
    }
  );

  // Update form data when editing
  useEffect(() => {
    if (editData) {
      setFormData(prevData => ({
        ...prevData,
        ...editData,
        features: editData.features || [''],
      }));
    }
  }, [editData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures,
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, ''],
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Validation
    if (type === 'plan') {
      if (!formData.name || !formData.price) {
        toast.error('Veuillez remplir tous les champs requis');
        return;
      }
    } else {
      if (!formData.memberId || !formData.planId) {
        toast.error('Veuillez sélectionner un membre et un plan');
        return;
      }
    }

    // 2. Construction du payload en fonction du contexte
    let payload;

    if (type === 'plan') {
      // Nettoyage des caractéristiques du plan
      payload = {
        ...formData,
        features: formData.features.filter((feature) => feature.trim() !== ''),
        price: parseFloat(formData.price) || 0,
      };
    } else {
      // Abonnement : récupérer les détails du plan sélectionné
      const selectedPlan = plans.find((p) => p.id === formData.planId);

      if (!selectedPlan) {
        toast.error("Plan d'abonnement introuvable");
        return;
      }

      const priceToUse = formData.customPrice
        ? parseFloat(formData.customPrice)
        : selectedPlan.price;

      // S’assurer que planType correspond aux valeurs acceptées par l’API
      const acceptedTypes = ['daily', 'weekly', 'monthly', 'quarterly', 'semiannual', 'annual'];
      let planTypeValue = selectedPlan.type || 'monthly';
      if (!acceptedTypes.includes(planTypeValue)) {
        // Tentative de déduction à partir du nom (ex: "Premium – 5000 DA" => monthly)
        const lowerName = selectedPlan.name.toLowerCase();
        if (lowerName.includes('jour')) planTypeValue = 'daily';
        else if (lowerName.includes('semaine')) planTypeValue = 'weekly';
        else if (lowerName.includes('mois') || lowerName.includes('mensuel')) planTypeValue = 'monthly';
        else if (lowerName.includes('trimestre') || lowerName.includes('trimestriel')) planTypeValue = 'quarterly';
        else if (lowerName.includes('semestre') || lowerName.includes('semestriel')) planTypeValue = 'semiannual';
        else if (lowerName.includes('an') || lowerName.includes('annuel')) planTypeValue = 'annual';
      }

      payload = {
        memberId: formData.memberId,
        planType: planTypeValue,
        planName: selectedPlan.name,
        price: priceToUse,
        duration: selectedPlan.duration || 30,
        startDate: formData.startDate,
        notes: formData.notes,
      };
    }

    // 3. Envoi au backend
    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const members = membersData?.members || [];
  const plans = plansData?.plans || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditing ? 'Modifier' : 'Créer'} {type === 'plan' ? 'un Plan' : 'un Abonnement'}
            </h1>
            <p className="text-white/60 mt-1">
              {type === 'plan' 
                ? 'Configurez les détails du plan d\'abonnement' 
                : 'Assignez un abonnement à un membre'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          {type === 'plan' ? (
            // Plan Form Fields
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Nom du Plan *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ex: Abonnement Mensuel"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Type de Plan *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="daily">Quotidien</option>
                    <option value="weekly">Hebdomadaire</option>
                    <option value="monthly">Mensuel</option>
                    <option value="quarterly">Trimestriel</option>
                    <option value="semiannual">Semestriel</option>
                    <option value="annual">Annuel</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Prix (DA) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="8500"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Durée
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="1 mois"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-white font-medium mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Description du plan..."
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Features */}
              <div className="mt-6">
                <label className="block text-white font-medium mb-2">
                  Caractéristiques
                </label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="Ajouter une caractéristique..."
                      className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeature}
                  className="mt-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Ajouter une caractéristique</span>
                </button>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Limite de Membres
                  </label>
                  <input
                    type="number"
                    name="maxMembers"
                    value={formData.maxMembers}
                    onChange={handleInputChange}
                    placeholder="Illimité si vide"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Niveau d'Accès
                  </label>
                  <select
                    name="accessLevel"
                    value={formData.accessLevel}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="basic">Basique</option>
                    <option value="premium">Premium</option>
                    <option value="vip">VIP</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isPopular"
                    checked={formData.isPopular}
                    onChange={handleInputChange}
                    className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-white">Plan populaire (mis en avant)</span>
                </label>
              </div>
            </>
          ) : (
            // Subscription Form Fields
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Membre *
                  </label>
                  <select
                    name="memberId"
                    value={formData.memberId}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un membre</option>
                    {members.map(member => (
                      <option
                        key={member.id}
                        value={member.id}
                      >
                        {member.firstName} {member.lastName} - {(member.code ?? member.memberCode) || ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Plan d'Abonnement *
                  </label>
                  <select
                    name="planId"
                    value={formData.planId}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un plan</option>
                    {plans.map(plan => (
                      <option
                        key={plan.id}
                        value={plan.id}
                      >
                        {plan.name} - {plan.price} DA
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Date de Début *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Prix Personnalisé (DA)
                  </label>
                  <input
                    type="number"
                    name="customPrice"
                    value={formData.customPrice}
                    onChange={handleInputChange}
                    placeholder="Laisser vide pour le prix du plan"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-white font-medium mb-2">
                  Mode de Paiement
                </label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">Espèces</option>
                  <option value="card">Carte bancaire</option>
                  <option value="transfer">Virement</option>
                  <option value="check">Chèque</option>
                </select>
              </div>

              <div className="mt-6">
                <label className="block text-white font-medium mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Notes supplémentaires..."
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 flex items-center space-x-2"
          >
            {(createMutation.isLoading || updateMutation.isLoading) && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            )}
            <span>
              {isEditing ? 'Mettre à jour' : 'Créer'} {type === 'plan' ? 'le Plan' : 'l\'Abonnement'}
            </span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default SubscriptionForm; 